import styled from '@emotion/styled';
import { Laptop, PeopleAlt, Person, PhoneAndroid, PhoneIphone, PhotoCamera, QrCode } from '@mui/icons-material';
import { ValuesType } from 'utility-types';
import { MenuButton } from '~/modules/Elements/Menu';
import { MicIconBlue, MicIconRed } from '~/modules/Elements/MicIcon';
import { typography } from '~/modules/Elements/cssMixins';
import styles from '~/modules/GameEngine/Drawing/styles';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import { MicSetupPreference, MobilePhoneModeSetting, useSettingValue } from '~/routes/Settings/SettingsState';

interface Props {
  onPreferenceSelected: (preference: ValuesType<typeof MicSetupPreference> | 'multiple-mics') => void;
  previouslySelected: string | null;
  skipText?: string;
  onBack?: () => void;
  context?: 'page' | 'preview';
}

function SelectPreference({ onPreferenceSelected, previouslySelected, onBack, skipText }: Props) {
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const { register } = useKeyboardNav({ onBackspace: onBack });

  return (
    <>
      <Option
        {...register(
          'remote-mics',
          () => onPreferenceSelected('remoteMics'),
          undefined,
          previouslySelected === 'remoteMics',
        )}>
        <OptionIconContainer>
          <PhoneAndroid />
          <PhoneIphone />
        </OptionIconContainer>

        <OptionContent>
          <OptionTitleRow>
            <OptionTitle>{mobilePhoneMode ? 'Connect other phones' : 'Use Smartphones'}</OptionTitle>
          </OptionTitleRow>

          <OptionDescription>
            Use{' '}
            <strong>
              <PhotoCamera /> Camera app
            </strong>{' '}
            to scan a{' '}
            <strong>
              <QrCode /> QR code
            </strong>{' '}
            that will open Remote Mic website - no need to download an app!
          </OptionDescription>
        </OptionContent>

        <PlayersNumber>
          <PeopleAlt />
          <strong>1-4</strong>
        </PlayersNumber>
      </Option>

      <Option
        {...register('built-in', () => onPreferenceSelected('built-in'), undefined, previouslySelected === 'built-in')}>
        <OptionIconContainer>
          <Person />
          {mobilePhoneMode ? <PhoneIphone /> : <Laptop />}
        </OptionIconContainer>

        <OptionContent>
          <OptionTitleRow>
            <OptionTitle>This {mobilePhoneMode ? "device's" : "computer's"} microphone</OptionTitle>
          </OptionTitleRow>

          <OptionDescription>
            Great to <strong>test</strong> the app, <strong>sing alone</strong> or don&#39;t care about the rivalry at
            the party
          </OptionDescription>
        </OptionContent>

        <PlayersNumber>
          <Person />
          <strong>1</strong>
        </PlayersNumber>
      </Option>

      <hr />

      {!mobilePhoneMode && (
        <Option
          {...register(
            'multiple-mics',
            () => onPreferenceSelected('multiple-mics'),
            undefined,
            previouslySelected === 'multiple-mics',
          )}>
          <OptionIconContainer>
            <MicIconBlue />
            <MicIconRed />
          </OptionIconContainer>

          <OptionContent>
            <OptionTitleRow>
              <OptionTitle>Multiple microphones</OptionTitle>
            </OptionTitleRow>

            <OptionDescription>
              Use either <strong>SingStar</strong> or <strong>multiple microphones</strong> connected to the device for
              each player
            </OptionDescription>
          </OptionContent>

          <PlayersNumber>
            <PeopleAlt />
            <strong>2</strong>
          </PlayersNumber>
        </Option>
      )}

      <Option
        {...register('advanced', () => onPreferenceSelected('advanced'), undefined, previouslySelected === 'advanced')}>
        <OptionIconContainer>
          <MicIconBlue />
          <PhoneIphone />
        </OptionIconContainer>

        <OptionContent>
          <OptionTitleRow>
            <OptionTitle>Advanced (manual) setup</OptionTitle>
          </OptionTitleRow>

          <OptionDescription>
            Assign the specific device to a player manually, e.g. if you don&#39;t have regular SingStar microphones.
          </OptionDescription>
        </OptionContent>

        <PlayersNumber>
          <PeopleAlt />
          <strong>1-4</strong>
        </PlayersNumber>
      </Option>

      <hr />

      <MenuButton {...register('skip', () => onPreferenceSelected('skip'), undefined, previouslySelected === 'skip')}>
        {skipText || 'Skip'}
      </MenuButton>
    </>
  );
}

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;

  padding-right: 1.5rem;

  @media (max-width: 640px) {
    padding-right: 0;
    overflow: visible;
  }
`;

const OptionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
`;

const OptionTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 640px) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    line-height: 1.15;
  }
`;

const PlayersNumber = styled.div`
  ${typography};
  gap: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  white-space: nowrap;
  flex: 0 0 auto;
  color: var(--walky-muted);

  svg {
    font-size: 0.9em;
  }

  position: absolute;
  inset: auto 0.95rem 0.85rem auto;
  opacity: 0.92;
  transition:
    transform 240ms ease,
    opacity 240ms ease;

  @media (max-width: 640px) {
    position: static;
    margin-left: 0;
    margin-top: 0.45rem;
    align-self: flex-start;
    font-size: 0.95em;
  }
`;

const OptionDescription = styled.div<{ focused?: boolean }>`
  font-size: 0.95rem;
  line-height: 1.3;
  max-height: 0;
  overflow: hidden;
  transition:
    max-height 320ms ease,
    opacity 260ms ease,
    padding-top 260ms ease,
    transform 260ms ease;
  opacity: 0;
  transform: translateY(-4px);
  color: var(--walky-muted);

  padding: 0 0 0 4.9rem;

  svg {
    font-size: 1.4rem;
  }

  strong {
    color: ${styles.colors.text.active};
  }

  @media (max-width: 640px) {
    padding: 0.45rem 0 0 0;
    opacity: 1;
    transform: none;
    max-height: none;
    overflow: visible;
    font-size: clamp(13px, 3.5vw, 15px);
    line-height: 1.25;
  }
`;

const OptionIconContainer = styled.div`
  position: relative;
  margin: 0rem 0.7rem 0 1.1rem;
  transition:
    transform 320ms ease,
    filter 320ms ease;
  flex: 0 0 auto;
  width: 4.8rem;

  svg {
    transition: 320ms ease;
    width: 3.6rem;
    height: 3.6rem;
    color: ${styles.colors.players[1].text};
    filter: drop-shadow(0 0 0 rgba(255, 180, 0, 0));
  }

  svg:first-of-type {
    top: 0.6rem;
    left: 1rem;
    position: absolute;
    color: ${styles.colors.players[0].text};
    z-index: 100;
    transform: scaleX(-1);
  }

  @media (max-width: 640px) {
    margin: 0.2rem 0.85rem 0 0.2rem;
    width: 4.2rem;

    svg {
      width: 28px;
      height: 28px;
    }

    svg:first-of-type {
      top: 0.3rem;
      left: 0.7rem;
    }
  }
`;

const Option = styled(MenuButton)<{ focused?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  color: var(--walky-ink);
  background: var(--walky-surface-raised);
  border: 1px solid var(--walky-border);
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);

  padding: 0.85rem 1rem !important;
  min-height: 5.8rem;

  transition:
    transform 220ms ease,
    background 260ms ease,
    box-shadow 260ms ease,
    border-color 260ms ease,
    filter 260ms ease;

  &:hover,
  &[data-focused='true'] {
    min-height: 8.8rem;
    color: var(--walky-ink);
    background: var(--walky-orange-soft);
    border-color: var(--walky-orange);
    box-shadow:
      0 10px 28px rgba(0, 0, 0, 0.28),
      0 0 0 1px rgba(255, 180, 0, 0.18),
      0 0 24px rgba(255, 180, 0, 0.08);

    transform: translateY(-1px);

    ${OptionDescription} {
      padding-top: 0.95rem;
      max-height: 4.8rem;
      opacity: 1;
      transform: translateY(0);
    }

    ${OptionIconContainer} {
      transform: scale(1.05);
      filter: drop-shadow(0 0 10px rgba(255, 180, 0, 0.12));

      svg {
        filter: drop-shadow(0 0 10px rgba(255, 180, 0, 0.1));
      }
    }

    ${PlayersNumber} {
      opacity: 1;
      transform: translateY(-1px);
    }
  }

  @media (max-width: 640px) {
    height: auto !important;
    min-height: 7.4rem;
    align-items: flex-start;
    padding: 0.95rem 0.9rem !important;
    flex-wrap: wrap;

    &:hover,
    &[data-focused='true'] {
      height: auto;
      transform: none;
      box-shadow:
        0 8px 20px rgba(0, 0, 0, 0.22),
        0 0 0 1px rgba(255, 180, 0, 0.14);

      ${OptionIconContainer} {
        transform: none;
        filter: none;
      }

      ${OptionDescription} {
        padding-top: 0.45rem;
        max-height: none;
        opacity: 1;
        transform: none;
      }

      ${PlayersNumber} {
        transform: none;
      }
    }
  }
`;

export default SelectPreference;
